import React, { useMemo } from 'react';
import { Box, Typography, Stack, Skeleton, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { createResourceColorById } from '../../../../util/resourceColorPool';

const ResourceLegend = ({ resources, loading, resourceColorById }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const fallbackColorById = useMemo(() => createResourceColorById(resources), [resources]);
  const effectiveColorById =
    resourceColorById instanceof Map && resourceColorById.size > 0
      ? resourceColorById
      : fallbackColorById;

  if (loading) {
    return (
      <Box
        sx={{
          width: '100%',
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          borderRadius: '8px 8px 0 0',
        }}
      >
        <Stack direction='row' spacing={2} flexWrap='wrap' useFlexGap>
          {[1, 2, 3, 4].map(i => (
            <Skeleton
              key={i}
              variant='rectangular'
              width={120}
              height={40}
              sx={{ borderRadius: 2 }}
            />
          ))}
        </Stack>
      </Box>
    );
  }

  if (!resources || resources.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        width: '100%',
        p: 0.5,
        borderBottom: 1,
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        boxShadow: 'none',
        borderRadius: 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          mb: 0.5,
          justifyContent: 'flex-start',
          width: '100%',
          // On small screens the legend lives inside the sticky grid header. With many
          // resources its wrapped rows would otherwise grow tall enough to push the actual
          // booking grid almost entirely out of the viewport. Cap the height and let the
          // legend scroll internally so the booking area stays usable.
          ...(isSmallScreen
            ? {
                maxHeight: '22vh',
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch',
                overscrollBehavior: 'contain',
              }
            : {}),
        }}
      >
        {resources.map(resource => {
          const ridRaw = resource?._id;
          const rid = ridRaw != null ? String(ridRaw) : '';
          const color = rid
            ? effectiveColorById.get(rid) || theme.palette.grey[300]
            : theme.palette.grey[300];
          return (
            <Box
              key={resource._id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.75,
                backgroundColor: 'background.paper',
                padding: '4px 8px',
                flex: '1 1 auto',
                minWidth: '130px',
                maxWidth: '100%',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    backgroundColor: color,
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant='body2'
                  sx={{
                    fontWeight: 500,
                    fontSize: '0.8rem',
                    color: 'text.primary',
                  }}
                >
                  {resource.name}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default ResourceLegend;
